import React, { useState, useEffect } from "react";
import SidebarUnlock from "./SidebarUnlock";
import { scheduleHint, HINTS } from "../utils/deferredHints";
import { useI18n } from "../contexts/I18nContext";

function Tooltip({ text, children }) {
  const [visible, setVisible] = useState(false);
  return (
    <div
      style={{ position: "relative", display: "block" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && text && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#222",
            color: "#fff",
            fontSize: "11px",
            lineHeight: "1.5",
            padding: "6px 10px",
            borderRadius: "6px",
            whiteSpace: "pre-wrap",
            maxWidth: "240px",
            width: "max-content",
            zIndex: 9999,
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            pointerEvents: "none",
          }}
        >
          {text}
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              border: "5px solid transparent",
              borderTopColor: "#222",
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * PasswordModal – Prepare-mode encryption password modal.
 *
 * Opens when user clicks "Verschlüsselung vorbereiten". Mutates the compose
 * item (subject, body, attachments) without sending. The user then sends manually.
 *
 * @param {string}   recipientSetKey      - Normalized "a@x.com;b@y.com" (display only)
 * @param {Array|null} decryptedEntries   - [{id, email, password}] from PM lookup
 * @param {string|null} prefillPassword   - Password pre-matched for recipientSetKey
 * @param {string|null} existingEntryId   - Existing PM entry id (for future save)
 * @param {boolean}  isUnlocked           - CryptoContext unlock state
 * @param {boolean}  passwortManagerAktiv - Whether PM feature is active
 * @param {object|null} settings          - BenutzerSettings
 * @param {boolean}  isPaid               - License type
 * @param {boolean}  hasAttachments       - Email has at least one attachment
 * @param {boolean}  allAttachmentsPdf    - All attachments are PDF files
 * @param {number}   [totalAttachmentBytes=0] - Total size of non-inline attachments in bytes
 * @param {Function} onClose              - Close the modal
 * @param {Function} onPrepare            - ({ mode, password, keepSubjectUnencrypted }) → called on confirm
 */
export default function PasswordModal({
  recipientSetKey = "",
  decryptedEntries = null,
  prefillPassword = null,
  existingEntryId: _existingEntryId = null,
  foundInPm = false,
  isUnlocked = false,
  passwortManagerAktiv = false,
  settings = null,
  isPaid = false,
  hasAttachments = false,
  allAttachmentsPdf = false,
  totalAttachmentBytes = 0,
  onClose,
  onPrepare,
}) {
  const { t } = useI18n();
  const [password, setPassword] = useState(prefillPassword || "");
  const [passwordConfirm, setPasswordConfirm] = useState(prefillPassword || "");
  const [selectedEntryId, setSelectedEntryId] = useState(""); // controlled dropdown
  const [showPassword, setShowPassword] = useState(false);
  const [keepSubjectUnencrypted, setKeepSubjectUnencrypted] = useState(
    settings?.betreffUnverschluesseltBehalten ?? false,
  );
  const [showUnlockInline, setShowUnlockInline] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendingMode, setSendingMode] = useState(null); // "full" | "pdfOnly"

  // Estimated duration:
  // - full mode: ~20s/MB + 10s overhead (container PDF build, release build)
  // - pdfOnly mode: ~10s/MB (no container, individual PDF encryption, release build)
  const estimatedSeconds = totalAttachmentBytes > 0
    ? sendingMode === "pdfOnly"
      ? Math.round(totalAttachmentBytes / (1024 * 1024) * 10)
      : Math.round(totalAttachmentBytes / (1024 * 1024) * 20 + 10)
    : null;
  const estimatedLabel = estimatedSeconds == null ? null
    : estimatedSeconds < 60
      ? t("ca. {seconds} Sekunden", { seconds: estimatedSeconds })
      : t(
          Math.round(estimatedSeconds / 60) > 1
            ? "ca. {minutes} Minuten"
            : "ca. {minutes} Minute",
          { minutes: Math.round(estimatedSeconds / 60) },
        );

  // Sync password fields + dropdown selection when the parent pushes a refreshed
  // sendFlowResult (e.g. after inline unlock → re-lookup → new decryptedEntries/prefillPassword).
  // Only overwrite if the user has not typed anything yet (password still matches the old prefill).
  useEffect(() => {
    if (prefillPassword) {
      setPassword(prefillPassword);
      setPasswordConfirm(prefillPassword);
    }
    // Pre-select dropdown entry whose password matches the prefill
    if (decryptedEntries && prefillPassword) {
      const matched = decryptedEntries.find((e) => e.password === prefillPassword);
      setSelectedEntryId(matched ? matched.id : "");
    } else {
      setSelectedEntryId("");
    }
  }, [prefillPassword, decryptedEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Schedule a deferred hint when PM found an entry but auto-prefill is off.
  // The hint will be shown the next time the taskpane loads.
  useEffect(() => {
    if (!prefillPassword && foundInPm && isUnlocked && decryptedEntries?.length > 0) {
      scheduleHint(HINTS.AUTO_PREFILL_TIP);
    }
  }, [foundInPm, prefillPassword, isUnlocked, decryptedEntries]);

  // Whether the PDF-only button is visible
  const showPdfOnlyButton =
    isPaid &&
    !!settings?.nurPdfDirektVerschluesseln &&
    hasAttachments &&
    allAttachmentsPdf;

  // Dropdown: populated from decryptedEntries when PM is unlocked
  const handleDropdownChange = (e) => {
    const selectedId = e.target.value;
    setSelectedEntryId(selectedId);
    if (!selectedId) {
      setPassword("");
      setPasswordConfirm("");
      return;
    }
    const entry = decryptedEntries?.find((en) => en.id === selectedId);
    if (entry) {
      setPassword(entry.password);
      setPasswordConfirm(entry.password);
    }
  };

  // After inline unlock succeeds, hide the unlock widget.
  // The parent will detect isUnlocked=true and re-render with fresh decryptedEntries + prefill.
  const handleAfterInlineUnlock = () => {
    setShowUnlockInline(false);
  };

  const validate = () => {
    if (!password) {
      setError(t("Bitte geben Sie ein Passwort ein."));
      return false;
    }
    if (password !== passwordConfirm) {
      setError(t("Die Passwörter stimmen nicht überein."));
      return false;
    }
    return true;
  };

  const handleEncryptAndSend = async (mode) => {
    if (!validate()) return;
    setError(null);
    setSending(true);
    setSendingMode(mode);
    try {
      await onPrepare({ mode, password, keepSubjectUnencrypted });
      onClose({ prepared: true, mode, usedPassword: password });
    } catch (err) {
      // USER_ABORTED = user cancelled a blocked-extension confirm.
      // Stay open silently — no further dialog needed.
      if (err.message === "USER_ABORTED" || err.code === "USER_ABORTED") return;

      console.error("[PasswordModal] Encryption failed:", err?.message, err?.code, err);

      if (
        err.code === "TOO_MANY_ATTACHMENTS" ||
        err.code === "ATTACHMENTS_TOO_LARGE" ||
        err.code === "PDF_ENGINE_CRASH"
      ) {
        setError(err.message);
      } else {
        setError(t("Die Verschlüsselung konnte nicht vorbereitet werden. Bitte versuchen Sie es erneut."));
      }
    } finally {
      setSending(false);
      setSendingMode(null);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Title */}
        <h3 style={styles.title}>{t("Verschlüsselung vorbereiten")}</h3>
        <p style={styles.description}>
          {t("Bitte geben Sie das Passwort ein. Die E-Mail wird im Entwurf vorbereitet und kann danach normal gesendet werden.")}
        </p>

        {/* Recipients display */}
        <label style={styles.label}>{t("Empfänger")}</label>
        <div style={styles.recipientBox}>
          {recipientSetKey || <em>{t("Keine Empfänger")}</em>}
        </div>

        {/* ── PM Dropdown (only if PM active AND unlocked AND has entries) ── */}
        {passwortManagerAktiv && isUnlocked && decryptedEntries?.length > 0 && (
          <div style={styles.dropdownSection}>
            <label style={styles.label}>{t("Gespeicherte Empfänger / Passwörter")}</label>
            <select
              style={styles.select}
              value={selectedEntryId}
              onChange={handleDropdownChange}
            >
              <option value="">{t("– Bitte wählen –")}</option>
              {(decryptedEntries || []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.email}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* ── Unlock hint (only if PM active AND locked) ── */}
        {passwortManagerAktiv && !isUnlocked && !showUnlockInline && (
          <Tooltip text={t("Wenn Sie Ihren Benutzer-Schlüssel eingeben, können Passwörter aus dem Passwortmanager automatisch angewendet werden.")}>
            <button
              style={{ ...styles.unlockHintButton, marginBottom: "16px", width: "100%" }}
              onClick={() => setShowUnlockInline(true)}
            >
              {t("Entsperren")}
            </button>
          </Tooltip>
        )}

        {/* ── Inline SidebarUnlock ── */}
        {passwortManagerAktiv && !isUnlocked && showUnlockInline && (
          <div style={styles.inlineUnlockWrapper}>
            <SidebarUnlock
              onSuccess={handleAfterInlineUnlock}
              onCancel={() => setShowUnlockInline(false)}
            />
          </div>
        )}

        {/* Prefill indicator */}
        {prefillPassword && (
          <div style={styles.successBox}>
            {t("Bestehendes Passwort für diese Empfänger gefunden und vorausgefüllt.")}
          </div>
        )}
        {!prefillPassword && foundInPm && isUnlocked && decryptedEntries?.length > 0 && (
          <div style={styles.infoBox}>
            {t("Für diese Empfänger wurde ein gespeichertes Passwort gefunden. Bitte wählen Sie es aus der Liste aus.")}
          </div>
        )}

        {/* Error */}
        {error && <div style={styles.errorBox}>{error}</div>}

        {/* Password fields */}
        <label style={styles.label}>{t("Passwort")}</label>
        <input
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError(null);
          }}
          placeholder={t("Passwort eingeben...")}
          style={styles.input}
          autoFocus
        />

        <label style={styles.label}>{t("Wiederholung")}</label>
        <input
          type={showPassword ? "text" : "password"}
          value={passwordConfirm}
          onChange={(e) => {
            setPasswordConfirm(e.target.value);
            setError(null);
          }}
          placeholder={t("Passwort wiederholen...")}
          style={styles.input}
        />

        {/* Checkboxes */}
        <label style={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
          />
          <span style={{ marginLeft: "6px" }}>{t("Kennwort anzeigen")}</span>
        </label>

        <label style={{ ...styles.checkboxLabel, ...(showPdfOnlyButton ? styles.checkboxDisabled : {}) }}>
          <input
            type="checkbox"
            checked={keepSubjectUnencrypted}
            onChange={(e) => setKeepSubjectUnencrypted(e.target.checked)}
            disabled={showPdfOnlyButton}
          />
          <span style={{ marginLeft: "6px" }}>{t("Betreff nicht verschlüsseln")}</span>
        </label>

        {/* ── Inline unencrypted confirm ── */}

        {/* ── Action buttons ── */}
        <div style={styles.actions}>
          <button style={styles.cancelButton} onClick={onClose}>
            {t("Abbrechen")}
          </button>

          {showPdfOnlyButton && (
            <Tooltip text={!password || !passwordConfirm ? t("Bitte geben Sie zuerst ein Passwort und die Wiederholung ein.") : ""}>
              <button
                style={{
                  ...styles.pdfOnlyButton,
                  ...(!password || !passwordConfirm || sending ? styles.buttonDisabled : {}),
                }}
                disabled={!password || !passwordConfirm || sending}
                onClick={() => handleEncryptAndSend("pdfOnly")}
              >
                {sendingMode === "pdfOnly" ? t("Wird vorbereitet...") : t("Nur PDF-Anhänge verschlüsseln (vorbereiten)")}
              </button>
            </Tooltip>
          )}

          <Tooltip text={!password || !passwordConfirm ? t("Bitte geben Sie zuerst ein Passwort und die Wiederholung ein.") : ""}>
            <button
              style={{
                ...styles.encryptButton,
                ...(!password || !passwordConfirm || sending || !recipientSetKey ? styles.buttonDisabled : {}),
              }}
              disabled={!password || !passwordConfirm || sending || !recipientSetKey}
              onClick={() => handleEncryptAndSend("full")}
            >
              {sendingMode === "full" ? t("Wird vorbereitet...") : t("Verschlüsselung vorbereiten")}
            </button>
          </Tooltip>
        </div>

        {sending && estimatedLabel && (
          <p style={styles.estimatedDuration}>{estimatedLabel}</p>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "0 12px",
  },
  modal: {
    background: "#fff",
    borderRadius: "8px",
    padding: "20px 16px",
    maxWidth: "340px",
    width: "calc(100% - 32px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  title: {
    margin: "0 0 8px 0",
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
  },
  description: {
    margin: "0 0 16px 0",
    fontSize: "13px",
    color: "#555",
    lineHeight: "1.5",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "6px",
  },
  recipientBox: {
    padding: "10px 12px",
    fontSize: "13px",
    color: "#555",
    backgroundColor: "#f8f9fa",
    border: "1px solid #dee2e6",
    borderRadius: "6px",
    marginBottom: "16px",
    wordBreak: "break-all",
    lineHeight: "1.5",
  },
  dropdownSection: {
    marginBottom: "16px",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    boxSizing: "border-box",
    backgroundColor: "#fff",
  },
  unlockHint: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "10px 14px",
    marginBottom: "16px",
    borderRadius: "6px",
    backgroundColor: "#cce5ff",
    border: "1px solid #b8daff",
    color: "#004085",
    fontSize: "13px",
    lineHeight: "1.5",
  },
  unlockHintButton: {
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#007bff",
    color: "#fff",
    cursor: "pointer",
  },
  inlineUnlockWrapper: {
    marginBottom: "16px",
    border: "1px solid #dee2e6",
    borderRadius: "6px",
    overflow: "hidden",
  },
  successBox: {
    padding: "8px 12px",
    marginBottom: "12px",
    borderRadius: "6px",
    backgroundColor: "#d4edda",
    border: "1px solid #c3e6cb",
    color: "#155724",
    fontSize: "13px",
  },
  infoBox: {
    padding: "8px 12px",
    marginBottom: "12px",
    borderRadius: "6px",
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    color: "#856404",
    fontSize: "13px",
  },
  errorBox: {
    padding: "8px 12px",
    marginBottom: "12px",
    borderRadius: "6px",
    backgroundColor: "#f8d7da",
    border: "1px solid #f5c6cb",
    color: "#721c24",
    fontSize: "13px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    boxSizing: "border-box",
    marginBottom: "12px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    color: "#555",
    marginBottom: "12px",
    cursor: "pointer",
  },
  checkboxDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "8px",
  },
  estimatedDuration: {
    margin: "8px 0 0 0",
    fontSize: "12px",
    color: "#666",
    textAlign: "center",
  },
  cancelButton: {
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "500",
    border: "1px solid #ccc",
    borderRadius: "6px",
    backgroundColor: "#fff",
    color: "#333",
    cursor: "pointer",
    width: "100%",
  },
  pdfOnlyButton: {
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#17a2b8",
    color: "#fff",
    cursor: "pointer",
    width: "100%",
  },
  encryptButton: {
    padding: "10px 18px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#28a745",
    color: "#fff",
    cursor: "pointer",
    width: "100%",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    backgroundColor: "#adb5bd",
    color: "#fff",
  },
};
